/**
 * This file was auto-generated.
 * Do not make direct changes to the file.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { z } from 'zod'

import * as CommonTypes from './common-types'

export type InventoryLocationListItem = {
	uuid: string
	name: string
	salesChannelCount: number
	currencyCode?: string
}
export type InventoryVariantSearchItem = {
	uuid: string
	sku?: string
	title?: string
	productTitle: string
	trackInventory: boolean
	quantityOnHand?: number
}
export type InventoryLocationDetail = {
	uuid: string
	name: string
	salesChannels: {
		uuid: string
		name: string
	}[]
}
export type BackfillInventoryLevelsResult = {
	created: number
	skipped: number
	remaining: number
	complete: boolean
}
export type InventorySettings = {
	enabled: boolean
	deductOnOrderComplete: boolean
	blockOversell: boolean
	restockOnReturn: boolean
	hideOutOfStock: boolean
}
export const InventoryStockStatus = {
	InStock: 'IN_STOCK',
	LowStock: 'LOW_STOCK',
	OutOfStock: 'OUT_OF_STOCK'
} as const
export type InventoryStockStatusType = (typeof InventoryStockStatus)[keyof typeof InventoryStockStatus]
export type InventoryLocationRef = {
	uuid: string
	name: string
}
export type VariantRef = {
	uuid: string
	sku?: string
	title?: string
	productTitle: string
	productUuid: string
	trackInventory: boolean
}
export type SupplierRef = {
	uuid: string
	name: string
}
export type MarginInfo = {
	retailPrice?: CommonTypes.Amount
	retailCurrencyCode?: string
	purchasePrice?: CommonTypes.Amount
	marginAmount?: number
	marginPercent?: number
	currencyCode?: string
	currencyMismatch?: boolean
}
export type InventoryLevelListItem = {
	uuid: string
	inventoryLocation: InventoryLocationRef
	variant: VariantRef
	quantityOnHand: number
	reorderPoint?: number
	reorderQuantity?: number
	stockStatus: InventoryStockStatusType
	preferredSupplier?: SupplierRef
	margin?: MarginInfo
	updatedAt: number
}
export type InventorySummary = {
	totalSkus: number
	lowStockCount: number
	outOfStockCount: number
}
export type Supplier = {
	uuid: string
	name: string
	currencyCode: string
	email?: string
	phone?: string
	externalId?: string
	createdAt: number
	updatedAt: number
}
export type VariantSupplier = {
	uuid: string
	supplier: Supplier
	variant: VariantRef
	purchasePrice: CommonTypes.Amount
	currencyCode: string
	supplierProductCode?: string
	isPreferred: boolean
	createdAt: number
	updatedAt: number
}
export type InventoryLevelDetail = {
	uuid: string
	inventoryLocation: InventoryLocationRef
	variant: VariantRef
	quantityOnHand: number
	reorderPoint?: number
	reorderQuantity?: number
	stockStatus: InventoryStockStatusType
	preferredSupplier?: SupplierRef
	margin?: MarginInfo
	variantSuppliers?: VariantSupplier[]
	createdAt: number
	updatedAt: number
}
export const StockMovementType = {
	Adjustment: 'ADJUSTMENT',
	Receipt: 'RECEIPT',
	TransferIn: 'TRANSFER_IN',
	TransferOut: 'TRANSFER_OUT',
	OrderDeduction: 'ORDER_DEDUCTION',
	OrderReturn: 'ORDER_RETURN'
} as const
export type StockMovementTypeType = (typeof StockMovementType)[keyof typeof StockMovementType]
export type StockMovementListItem = {
	uuid: string
	type: StockMovementTypeType
	inventoryLocation: InventoryLocationRef
	variant: VariantRef
	quantityDelta: number
	quantityAfter: number
	reason?: string
	orderUuid?: string
	purchaseOrderUuid?: string
	inventoryTransferUuid?: string
	createdByUserName?: string
	createdAt: number
}
export const PurchaseOrderStatus = {
	Draft: 'DRAFT',
	Ordered: 'ORDERED',
	PartiallyReceived: 'PARTIALLY_RECEIVED',
	Received: 'RECEIVED',
	Cancelled: 'CANCELLED'
} as const
export type PurchaseOrderStatusType = (typeof PurchaseOrderStatus)[keyof typeof PurchaseOrderStatus]
export type PurchaseOrderListItem = {
	uuid: string
	referenceNumber?: string
	status: PurchaseOrderStatusType
	currencyCode: string
	supplierName?: string
	inventoryLocation: InventoryLocationRef
	lineCount: number
	expectedAt?: number
	createdAt: number
	updatedAt: number
}
export type PurchaseOrderLine = {
	uuid: string
	variant: VariantRef
	orderedQuantity: number
	receivedQuantity: number
	unitCost?: CommonTypes.Amount
}
export type PurchaseOrderDetail = {
	uuid: string
	referenceNumber?: string
	status: PurchaseOrderStatusType
	currencyCode: string
	supplier?: Supplier
	inventoryLocation: InventoryLocationRef
	lines: PurchaseOrderLine[]
	expectedAt?: number
	notes?: string
	createdAt: number
	updatedAt: number
}
export const InventoryTransferStatus = {
	Draft: 'DRAFT',
	InTransit: 'IN_TRANSIT',
	Received: 'RECEIVED',
	Cancelled: 'CANCELLED'
} as const
export type InventoryTransferStatusType = (typeof InventoryTransferStatus)[keyof typeof InventoryTransferStatus]
export type InventoryTransferListItem = {
	uuid: string
	status: InventoryTransferStatusType
	fromInventoryLocation: InventoryLocationRef
	toInventoryLocation: InventoryLocationRef
	lineCount: number
	shippedAt?: number
	receivedAt?: number
	createdAt: number
	updatedAt: number
}
export type InventoryTransferLine = {
	uuid: string
	variant: VariantRef
	quantity: number
}
export type InventoryTransferDetail = {
	uuid: string
	status: InventoryTransferStatusType
	fromInventoryLocation: InventoryLocationRef
	toInventoryLocation: InventoryLocationRef
	lines: InventoryTransferLine[]
	shippedAt?: number
	receivedAt?: number
	notes?: string
	createdAt: number
	updatedAt: number
}
export type InventoryReportSummary = {
	totalSkus: number
	totalQuantityOnHand: number
	lowStockCount: number
	outOfStockCount: number
	topLowStock: InventoryLevelListItem[]
}

export const zInventoryLocationListItem = z.object({
	uuid: z.string(),
	name: z.string(),
	salesChannelCount: z.number(),
	currencyCode: z.string().optional()
})
export const zInventoryVariantSearchItem = z.object({
	uuid: z.string(),
	sku: z.string().optional(),
	title: z.string().optional(),
	productTitle: z.string(),
	trackInventory: z.boolean(),
	quantityOnHand: z.number().optional()
})
export const zInventoryLocationDetail = z.object({
	uuid: z.string(),
	name: z.string(),
	salesChannels: z.array(
		z.object({
			uuid: z.string(),
			name: z.string()
		})
	)
})
export const zBackfillInventoryLevelsResult = z.object({
	created: z.number(),
	skipped: z.number(),
	remaining: z.number(),
	complete: z.boolean()
})
export const zInventorySettings = z.object({
	enabled: z.boolean(),
	deductOnOrderComplete: z.boolean(),
	blockOversell: z.boolean(),
	restockOnReturn: z.boolean(),
	hideOutOfStock: z.boolean()
})
export const zInventoryStockStatus = z.enum(['IN_STOCK', 'LOW_STOCK', 'OUT_OF_STOCK'])
export const zInventoryLocationRef = z.object({
	uuid: z.string(),
	name: z.string()
})
export const zVariantRef = z.object({
	uuid: z.string(),
	sku: z.string().optional(),
	title: z.string().optional(),
	productTitle: z.string(),
	productUuid: z.string(),
	trackInventory: z.boolean()
})
export const zSupplierRef = z.object({
	uuid: z.string(),
	name: z.string()
})
export const zMarginInfo = z.object({
	retailPrice: CommonTypes.zAmount.optional().nullable(),
	retailCurrencyCode: z.string().optional(),
	purchasePrice: CommonTypes.zAmount.optional().nullable(),
	marginAmount: z.number().optional(),
	marginPercent: z.number().optional(),
	currencyCode: z.string().optional(),
	currencyMismatch: z.boolean().optional()
})
export const zInventoryLevelListItem = z.object({
	uuid: z.string(),
	inventoryLocation: zInventoryLocationRef,
	variant: zVariantRef,
	quantityOnHand: z.number(),
	reorderPoint: z.number().optional(),
	reorderQuantity: z.number().optional(),
	stockStatus: zInventoryStockStatus,
	preferredSupplier: zSupplierRef.optional().nullable(),
	margin: zMarginInfo.optional().nullable(),
	updatedAt: z.number()
})
export const zInventorySummary = z.object({
	totalSkus: z.number(),
	lowStockCount: z.number(),
	outOfStockCount: z.number()
})
export const zSupplier = z.object({
	uuid: z.string(),
	name: z.string(),
	currencyCode: z.string(),
	email: z.string().optional(),
	phone: z.string().optional(),
	externalId: z.string().optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zVariantSupplier = z.object({
	uuid: z.string(),
	supplier: zSupplier,
	variant: zVariantRef,
	purchasePrice: CommonTypes.zAmount,
	currencyCode: z.string(),
	supplierProductCode: z.string().optional(),
	isPreferred: z.boolean(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zInventoryLevelDetail = z.object({
	uuid: z.string(),
	inventoryLocation: zInventoryLocationRef,
	variant: zVariantRef,
	quantityOnHand: z.number(),
	reorderPoint: z.number().optional(),
	reorderQuantity: z.number().optional(),
	stockStatus: zInventoryStockStatus,
	preferredSupplier: zSupplierRef.optional().nullable(),
	margin: zMarginInfo.optional().nullable(),
	variantSuppliers: z.array(zVariantSupplier).optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zStockMovementType = z.enum([
	'ADJUSTMENT',
	'RECEIPT',
	'TRANSFER_IN',
	'TRANSFER_OUT',
	'ORDER_DEDUCTION',
	'ORDER_RETURN'
])
export const zStockMovementListItem = z.object({
	uuid: z.string(),
	type: zStockMovementType,
	inventoryLocation: zInventoryLocationRef,
	variant: zVariantRef,
	quantityDelta: z.number(),
	quantityAfter: z.number(),
	reason: z.string().optional(),
	orderUuid: z.string().optional(),
	purchaseOrderUuid: z.string().optional(),
	inventoryTransferUuid: z.string().optional(),
	createdByUserName: z.string().optional(),
	createdAt: z.number()
})
export const zPurchaseOrderStatus = z.enum(['DRAFT', 'ORDERED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'])
export const zPurchaseOrderListItem = z.object({
	uuid: z.string(),
	referenceNumber: z.string().optional(),
	status: zPurchaseOrderStatus,
	currencyCode: z.string(),
	supplierName: z.string().optional(),
	inventoryLocation: zInventoryLocationRef,
	lineCount: z.number(),
	expectedAt: z.number().optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zPurchaseOrderLine = z.object({
	uuid: z.string(),
	variant: zVariantRef,
	orderedQuantity: z.number(),
	receivedQuantity: z.number(),
	unitCost: CommonTypes.zAmount.optional().nullable()
})
export const zPurchaseOrderDetail = z.object({
	uuid: z.string(),
	referenceNumber: z.string().optional(),
	status: zPurchaseOrderStatus,
	currencyCode: z.string(),
	supplier: zSupplier.optional().nullable(),
	inventoryLocation: zInventoryLocationRef,
	lines: z.array(zPurchaseOrderLine),
	expectedAt: z.number().optional(),
	notes: z.string().optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zInventoryTransferStatus = z.enum(['DRAFT', 'IN_TRANSIT', 'RECEIVED', 'CANCELLED'])
export const zInventoryTransferListItem = z.object({
	uuid: z.string(),
	status: zInventoryTransferStatus,
	fromInventoryLocation: zInventoryLocationRef,
	toInventoryLocation: zInventoryLocationRef,
	lineCount: z.number(),
	shippedAt: z.number().optional(),
	receivedAt: z.number().optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zInventoryTransferLine = z.object({
	uuid: z.string(),
	variant: zVariantRef,
	quantity: z.number()
})
export const zInventoryTransferDetail = z.object({
	uuid: z.string(),
	status: zInventoryTransferStatus,
	fromInventoryLocation: zInventoryLocationRef,
	toInventoryLocation: zInventoryLocationRef,
	lines: z.array(zInventoryTransferLine),
	shippedAt: z.number().optional(),
	receivedAt: z.number().optional(),
	notes: z.string().optional(),
	createdAt: z.number(),
	updatedAt: z.number()
})
export const zInventoryReportSummary = z.object({
	totalSkus: z.number(),
	totalQuantityOnHand: z.number(),
	lowStockCount: z.number(),
	outOfStockCount: z.number(),
	topLowStock: z.array(zInventoryLevelListItem)
})
